import { ChangeDetectorRef, Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { catchError, filter, map, of, switchMap, take } from 'rxjs';
import { AddressTypes, CitySearchResponse, CitySearchResult, StreetSearchResponse, StreetSearchResult, WarehouseResult } from '@models/address.model';
import { Unsubscriber } from '@shared/classes/unsubscriber';
import { AddressService } from '../address.service';
import { InternetOrder } from '../../models/internet-order.model';
import {
  getAddressTypeByWarehouseCategory,
  getOrderPostboxNumber,
  getOrderRecipientCityName,
  isOrderDeliveryToDoors,
} from './prom-order.helpers';

/**
 * Substitutes an internet order (Prom) into the invoice form's second step:
 * patches the recipient contact and pre-fills the recipient address
 * (doors, warehouse, store or postbox) from the order's delivery data.
 */
@Injectable()
export class PromOrderService extends Unsubscriber {
  private order: InternetOrder;

  private stepAddressForm: UntypedFormGroup;

  constructor(
    private addressService: AddressService,
    private cd: ChangeDetectorRef,
  ) {
    super();
  }

  private get addressRecipientControl(): AbstractControl {
    return this.stepAddressForm.get('AddressRecipient');
  }

  get hasOrder(): boolean {
    return !!this.order;
  }

  substituteOrderData(order: InternetOrder, stepAddressForm: UntypedFormGroup): void {
    if (!order) {
      return;
    }

    this.order = order;
    this.stepAddressForm = stepAddressForm;

    this.stepAddressForm.get('NewRecipientPrivateContact').patchValue({
      phone: this.order.recipientContactPhone,
      lastName: this.order.recipient.lastName,
      firstName: this.order.recipient.firstName,
      middleName: this.order.recipient.middleName,
    });

    if (isOrderDeliveryToDoors(this.order)) {
      this.substituteRecipientDoorsAddress();
    } else {
      this.substituteRecipientPickupPoint();
    }
  }

  /**
   * Substitutes a Prom order doors address: resolves the city and its streets
   * first, and only then touches the form. At substitution time the recipient
   * address block is still in its pre-init 'select' mode without the doors
   * sub-form (city/street/building controls don't exist yet), so the type
   * switch and control lookups must happen after the requests resolve —
   * the same way substituteRecipientPickupPoint does it.
   */
  private substituteRecipientDoorsAddress(): void {
    const cityId = this.order.recipientCityId;
    const cityName = getOrderRecipientCityName(this.order);
    const streetName = this.order.recipientStreetName;
    const buildingNumber = this.order.recipientBuildingNumber;
    const apartmentNumber = this.order.recipientApartmentNumber;

    this.subscriptions = this.addressService
      .getCities({ name: cityName })
      .pipe(
        map((res: CitySearchResponse) => this.findCityForOrder(res.Addresses || [], cityId)),
        switchMap((city: CitySearchResult | null) => {
          if (!city) {
            return of<{ city: CitySearchResult; streets: StreetSearchResult[] } | null>(null);
          }

          return this.addressService
            .getStreets({ name: streetName, localityRef: city.Ref })
            .pipe(map((res: StreetSearchResponse) => ({ city, streets: res.Addresses || [] })));
        }),
        catchError(() => of<{ city: CitySearchResult; streets: StreetSearchResult[] } | null>(null)),
        take(1),
      )
      .subscribe((addressData) => {
        if (!addressData) {
          return;
        }

        const recipientAddressTypeControl = this.addressRecipientControl.get('type');
        if (recipientAddressTypeControl && recipientAddressTypeControl.value !== AddressTypes.DOORS) {
          recipientAddressTypeControl.setValue(AddressTypes.DOORS);
          this.cd.detectChanges();
        }

        const addressRecipientDataControl = this.addressRecipientControl.get('data');
        const recipientCityControl = addressRecipientDataControl?.get('city');
        const recipientStreetControl = addressRecipientDataControl?.get('street');
        if (!recipientCityControl || !recipientStreetControl) {
          return;
        }

        // Setting the city synchronously resets and enables the street control,
        // so the street must be substituted after the city.
        recipientCityControl.setValue(addressData.city);
        this.cd.detectChanges();

        const street = addressData.streets.find(
          (s) => s.SettlementStreetDescription?.toLowerCase() === streetName.toLowerCase(),
        );
        if (!street) {
          return;
        }

        recipientStreetControl.setValue(street);
        this.cd.detectChanges();

        if (buildingNumber) {
          addressRecipientDataControl.get('building')?.setValue(buildingNumber);
        }

        if (apartmentNumber) {
          addressRecipientDataControl.get('room')?.setValue(apartmentNumber);
        }
      });
  }

  /**
   * Substitutes a Prom order pickup point (warehouse, store or postbox):
   * finds the warehouse by its ref among the recipient city's warehouses,
   * derives the address type from the warehouse's category, and resolves the
   * matching city option via searchSettlements by the warehouse's settlement.
   */
  private substituteRecipientPickupPoint(): void {
    const { recipientCityId, recipientWarehouseId } = this.order;

    /**
     * Prom order includes next data: for warehouse-like pickup points (warehouse, store, postbox) it has
     * `recipientWarehouseId` and `recipientCityId`, for doors addresses it has `recipientCityId` and
     * `recipientStreetName` (and optionally `recipientBuildingNumber` and `recipientApartmentNumber`).
     *  - recipientWarehouseId - is a warehouse Ref
     *  - recipientCityId - is a city reference (DeliveryCity)
     *
     * That's why we do the next order of calls:
     *   * 1. getWarehouses(recipientCityId) - to get all warehouses in the city
     *   * 2. find the warehouse with Ref === recipientWarehouseId
     *   * 3. getCities({ settlementRef: warehouse.SettlementRef }) - to get the city by the warehouse's settlement reference
     *   * 4. patch the form controls with the found city and warehouse
     *
     * That is a correct order of calls, because of available data from Prom order. We can't get the city by
     * recipientCityId, because it is a DeliveryCity reference, not a settlement reference. So we need to get the
     * warehouse first, and then get the city by the warehouse's settlement reference.
     *
     * For a postbox address the city may hold thousands of warehouses; searching by the
     * postbox number narrows the response to the single matching postomat.
     */

    const postboxNumber = getOrderPostboxNumber(this.order);

    this.subscriptions = this.addressService
      .getWarehouses(
        recipientCityId,
        null,
        undefined,
        false,
        undefined,
        postboxNumber || undefined,
      )
      .pipe(
        map((warehouses: WarehouseResult[]) =>
          warehouses.find((warehouse) => warehouse.Ref === recipientWarehouseId)),
        switchMap((warehouse: WarehouseResult | undefined) => {
          if (!warehouse) {
            return of(null);
          }

          return this.addressService
            .getCities({ settlementRef: warehouse.SettlementRef }, 1, 100)
            .pipe(map((res: CitySearchResponse) => {
              const city = res.Addresses?.[0];
              return city ? { city, warehouse } : null;
            }));
        }),
        catchError(() => of<{ city: CitySearchResult; warehouse: WarehouseResult } | null>(null)),
        take(1),
      )
      .subscribe((addressData) => {
        if (!addressData) {
          return;
        }

        const addressType = getAddressTypeByWarehouseCategory(addressData.warehouse.CategoryOfWarehouse);
        const recipientAddressTypeControl = this.addressRecipientControl.get('type');
        if (recipientAddressTypeControl && recipientAddressTypeControl.value !== addressType) {
          recipientAddressTypeControl.setValue(addressType);
          this.cd.detectChanges();
        }

        const addressRecipientDataControl = this.addressRecipientControl.get('data');
        const recipientCityControl = addressRecipientDataControl?.get('city');
        const recipientPickupPointControl = addressRecipientDataControl?.get(addressType.toLowerCase());
        if (!recipientCityControl || !recipientPickupPointControl) {
          return;
        }

        recipientCityControl.setValue(addressData.city);
        this.cd.detectChanges();

        if (addressType === AddressTypes.POSTBOX) {
          // Postbox select resets synchronously (emitEvent: false) on city change,
          // so the value can be substituted directly.
          recipientPickupPointControl.setValue(addressData.warehouse);
          return;
        }

        // Warehouse/store selects wipe the control with a debounced reset('') when the
        // city's warehouse list starts loading; substitute only after that reset.
        this.subscriptions = recipientPickupPointControl.valueChanges
          .pipe(
            filter((value) => !value),
            take(1),
          )
          .subscribe(() => {
            if (recipientCityControl.value === addressData.city) {
              recipientPickupPointControl.setValue(addressData.warehouse);
            }
          });
      });
  }

  /** Picks the internet order's city from settlement search results by its delivery city ref. */
  private findCityForOrder(list: CitySearchResult[], cityId: string): CitySearchResult | null {
    return (cityId && list.find((city) => city.DeliveryCity === cityId)) || null;
  }
}
